import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import texturePath from '../../assets/img/textures/decal.png';
import CustomShaderMaterial from './CustomShaderMaterial';
import { coloursToArray } from '../../utils';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.mouse = new THREE.Vector2(0, 0);
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/f8ffe5-06d6a0-1b9aaa-ef476f-ffc43d');//('https://coolors.co/d5573b-885053-777da7-94c9a9-c6ecae');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.domElem.appendChild(this.renderer.domElement);

        this.disposed = false;
        this.scene.background = this.colourArr[3];
        this.scene.fog = new THREE.Fog(this.colourArr[0], 2, 200);

        this.setupCamera();
        this.setupLight();
        this.setupControls();
        this.setupSceneSubjects();
    }

    setupCamera() {
        let VIEW_ANGLE = 70;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 0.1;
        let FAR_PLANE = 100;

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.set(0, 2, 5);
    }

    setupLight() {
        let pointLight = new THREE.PointLight(this.colourArr[3], 2.);
        pointLight.position.set(1.5, 1.5, 2.5);
        this.scene.add(pointLight);

        let pointLight2 = new THREE.PointLight(this.colourArr[0], 1.);
        pointLight2.position.set(-1.5, 0, -2.5);
        this.scene.add(pointLight2);

        let pointLight3 = new THREE.PointLight(this.colourArr[1], 1.);
        pointLight3.position.set(0.5, -1, 0);
        this.scene.add(pointLight3);

        let hemiLight = new THREE.HemisphereLight(this.colourArr[2], this.colourArr[3], 1);
        hemiLight.position.set(0, 0.5, 0);
        this.scene.add(hemiLight);

        let dirLight = new THREE.DirectionalLight(this.colourArr[1], 1);
        this.scene.add(dirLight);
    }

    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.autoRotate = true;
        this.controls.autoRotateSpeed = 0.5;
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.enableZoom = true;
        this.controls.enablePan = true;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.5;
        this.controls.maxDistance = 10;
        this.controls.minDistance = 1;
    }

    loadTextures(path) {
        let textureLoader = new THREE.TextureLoader();
        let texture = textureLoader.load(path, (texture) => {
            return texture;
        });
        return texture;
    }

    setupSceneSubjects() {
        const geo = new THREE.IcosahedronBufferGeometry(1, 4);
        const edgeGeo = new THREE.EdgesGeometry(geo);
        const texture = this.loadTextures(texturePath);
        this.material = new CustomShaderMaterial(this.colourArr[1], texture);

        const mesh = new THREE.Mesh(geo, this.material.makeMaterial());
        const edgeLines = new THREE.LineSegments(edgeGeo, this.material.makeLineMaterial());
        const meshPoints = new THREE.Points(geo, this.material.makePointsMaterial());
        meshPoints.scale.set(2, 2, 2);
        this.scene.add(mesh);
        this.scene.add(edgeLines);
        this.scene.add(meshPoints);
    }

    update() {
        if (this.disposed) return;
        if (this.controls) this.controls.update();
        if (this.material) this.material.update();
        this.renderer.render(this.scene, this.camera);
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    onMouseMove(e) {
        this.mouse.x = (e.clientX - window.innerWidth / 2) / 100;
        this.mouse.y = (e.clientY - window.innerHeight / 2) / 100;
        this.camera.position.x += (this.mouse.x - this.camera.position.x) * .05;
        this.camera.position.y += (-this.mouse.y - this.camera.position.y) * .05;
    }

    dispose() {
        this.disposed = true;
        this.scene.children.forEach(child => {
            if (child.isMesh) {
                child.geometry.dispose();
            }
        });
        this.scene.dispose();
        this.renderer.dispose();
        this.domElem.removeChild(this.renderer.domElement);
    };
}

